import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { isHomeImageLoaded, markHomeImageLoaded } from './homeLoadedImages';
import {
  buildHomeImageSrcSet,
  homeImageSizes,
  resolveHomeImageUrl,
} from './homeImageDelivery';
import { claimHomeImagePriority } from './homeImagePriority';

type HomeImageProfile = 'hero' | 'card' | 'rail' | 'lookbook';

type HomeStaticImageProps = Readonly<{
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  fallbackSrc?: string;
  /** Above-the-fold — subject to global 4-image high-priority budget. */
  priority?: boolean;
  profile?: HomeImageProfile;
}>;

function imageIsReady(img: HTMLImageElement): boolean {
  return img.complete && img.naturalWidth > 0;
}

/**
 * Static marketing image for home sections below the hero.
 * Resolves CDN URLs when configured; lazy by default with session decode cache.
 */
export function HomeStaticImage({
  src,
  alt,
  className = 'h-full w-full object-cover',
  style,
  fallbackSrc,
  priority = false,
  profile = 'card',
}: HomeStaticImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const resolvedSrc = resolveHomeImageUrl(src);
  const srcSet = buildHomeImageSrcSet(src);
  const sizes = srcSet ? homeImageSizes(profile) : undefined;
  const highPriority = claimHomeImagePriority(priority);
  const [ready, setReady] = useState(() => isHomeImageLoaded(resolvedSrc));

  useEffect(() => {
    const cached = isHomeImageLoaded(resolvedSrc);
    if (cached) {
      setReady(true);
      return;
    }

    const el = imgRef.current;
    if (el && imageIsReady(el)) {
      markHomeImageLoaded(resolvedSrc);
      setReady(true);
    }
  }, [resolvedSrc]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !ready) return;

    const restoreIfEvicted = () => {
      if (imageIsReady(el)) return;
      const current = el.currentSrc || el.src;
      if (!current) return;
      void el.decode().catch(() => {
        el.src = current;
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) restoreIfEvicted();
      },
      { rootMargin: '320px', threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ready, resolvedSrc]);

  if (!src) return null;

  const eager = highPriority || ready;

  return (
    <img
      ref={imgRef}
      src={resolvedSrc}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      data-loaded={ready ? 'true' : undefined}
      className={`home-static-image ${ready ? 'home-static-image--ready' : ''} ${className}`}
      style={style}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...(highPriority ? { fetchpriority: 'high' as const } : { fetchpriority: 'auto' as const })}
      referrerPolicy="no-referrer"
      onLoad={(event) => {
        const el = event.currentTarget;
        if (!imageIsReady(el)) return;
        markHomeImageLoaded(resolvedSrc);
        setReady(true);
      }}
      onError={(event) => {
        const el = event.currentTarget;
        if (fallbackSrc && el.src !== fallbackSrc) {
          el.src = fallbackSrc;
          return;
        }
        if (el.src !== src && el.src !== resolvedSrc) {
          el.src = optimizeFallback(src);
          return;
        }
        el.classList.add('home-static-image--broken');
      }}
    />
  );
}

function optimizeFallback(url: string): string {
  return resolveHomeImageUrl(url);
}
